import { itemReferenceKind } from '../../constants/enum';
import { type ItemListType } from '../../types/common';
import { type DictionaryWithMappingType } from '../../types/dictionary';
import { type MasterWithMappingType } from '../../types/master';
import { resolveSearchKey } from '../master/matchKeys';

/**
 * 項目がマスタ照合・辞書変換の設定から参照されている箇所を洗い出す（#3344）。
 *
 * 背景: マスタ・辞書と項目の間に直接のリレーションはなく、pattern_id と item_id が
 * 唯一の接合点である。項目を削除しても参照は残り続け、syncLabels は
 * 「item_id がマップに無ければラベルを変更しない」仕様のため、
 * 削除済み項目の古い名前が UI に表示され続ける。
 *
 * ここで参照を検出し、照合キーなら削除をブロック、それ以外なら警告のうえ
 * 保存時に自動解除する（仕様書 §2）。
 */

export type ItemReferenceKindType = (typeof itemReferenceKind)[keyof typeof itemReferenceKind];

export type ItemReference = {
  kind: ItemReferenceKindType;
  /** 参照元の名前（マスタ／辞書のファイル名）。警告文に出す */
  sourceName: string;
  /** 参照されている項目の item_id。複数項目をまとめて判定したとき、どれが該当したかを示す */
  itemId: string;
  /**
   * 参照されている項目の表示名。マッピングが保持しているラベルを使う。
   * 親子項目は「親 > 子」形式（buildItemLabelMap と同じ）。
   * 旧データでラベルが空のことがあるため、呼び出し側は空文字を想定する。
   */
  itemLabel: string;
  /** マスタ由来の参照のみ設定される */
  masterId?: string;
  /** 辞書由来の参照のみ設定される */
  dictionaryId?: string;
  mappingId: string;
};

/** 参照箇所の表示文言。警告文の箇条書きに使う */
export const itemReferenceKindLabels: Record<ItemReferenceKindType, string> = {
  [itemReferenceKind.masterSearchKey]: '照合キー',
  [itemReferenceKind.dictionarySearchKey]: '照合元項目',
  [itemReferenceKind.masterFilterKey]: '絞り込み条件',
  [itemReferenceKind.masterColumnMapping]: '反映先',
  [itemReferenceKind.dictionaryColumnMapping]: '反映先',
  [itemReferenceKind.masterScopeKey]: '照合履歴の分離キー',
};

/**
 * 削除をブロックする参照種別。
 * 照合キーが欠けたマッピングは照合処理が成立せず、resolveSearchKey にも救済がないため、
 * システムが勝手に消すのではなく利用者に明示的にマスタ側の設定を外してもらう（仕様書 §2.1）。
 */
const blockingKinds: ReadonlySet<ItemReferenceKindType> = new Set([
  itemReferenceKind.masterSearchKey,
  itemReferenceKind.dictionarySearchKey,
]);

/**
 * 指定した item_id 群を参照している箇所を列挙する。
 *
 * 複数IDを受けるのは、子項目を持つ親項目を削除する場合に子の item_id も
 * 一緒に消えるため。マッピングが参照するのは子の id なので
 * （buildItemLabelMap の規則: 子ありなら子IDごと・子なしなら親ID）、
 * 親のIDだけで判定すると子の参照を見落とす。
 *
 * マスタ照合は新旧2形式が混在し移行しない方針のため、
 * 検索キーの読み取りは必ず resolveSearchKey を通す（旧形式 search_item_id を読み替えるため）。
 */
export const findItemReferences = (
  itemIds: ReadonlySet<string>,
  masters: MasterWithMappingType[],
  dictionaries: DictionaryWithMappingType[],
): ItemReference[] => {
  if (itemIds.size === 0) return [];

  const references: ItemReference[] = [];
  const isTarget = (itemId: string | undefined): boolean => !!itemId && itemIds.has(itemId);

  for (const master of masters) {
    const base = {
      sourceName: master.file_name,
      masterId: master.master_id,
      mappingId: master.mapping_id,
    };

    const searchKey = resolveSearchKey(master);
    if (isTarget(searchKey.item_id)) {
      references.push({
        ...base,
        kind: itemReferenceKind.masterSearchKey,
        itemId: searchKey.item_id,
        itemLabel: searchKey.item_label,
      });
    }

    // 絞り込みキーは raw を見る。正規化（resolveFilterKeys）は csv_column 欠落の要素を
    // 落とすため、その要素しか参照がない項目を「参照なし」と誤判定してしまう。
    for (const key of master.filter_keys ?? []) {
      if (isTarget(key?.item_id)) {
        references.push({
          ...base,
          kind: itemReferenceKind.masterFilterKey,
          itemId: key.item_id,
          itemLabel: key.item_label ?? '',
        });
      }
    }

    for (const mapping of master.column_mappings ?? []) {
      if (isTarget(mapping?.item_id)) {
        references.push({
          ...base,
          kind: itemReferenceKind.masterColumnMapping,
          itemId: mapping.item_id,
          itemLabel: mapping.item_label ?? '',
        });
      }
    }

    if (isTarget(master.scope_item_id)) {
      references.push({
        ...base,
        kind: itemReferenceKind.masterScopeKey,
        itemId: master.scope_item_id ?? '',
        itemLabel: master.scope_item_label ?? '',
      });
    }
  }

  for (const dictionary of dictionaries) {
    const base = {
      sourceName: dictionary.file_name,
      dictionaryId: dictionary.dictionary_id,
      mappingId: dictionary.mapping_id,
    };

    if (isTarget(dictionary.search_item_id)) {
      references.push({
        ...base,
        kind: itemReferenceKind.dictionarySearchKey,
        itemId: dictionary.search_item_id,
        itemLabel: dictionary.search_item_label ?? '',
      });
    }

    for (const mapping of dictionary.column_mappings ?? []) {
      if (isTarget(mapping?.item_id)) {
        references.push({
          ...base,
          kind: itemReferenceKind.dictionaryColumnMapping,
          itemId: mapping.item_id,
          itemLabel: mapping.item_label ?? '',
        });
      }
    }
  }

  return references;
};

/**
 * 削除対象の項目が占める item_id を集める。
 *
 * 親項目（子あり）を削除すると子の item_id がまとめて消えるため、子のIDを返す。
 * マッピングが参照するのは子の id であり、親のIDだけでは参照を見落とす。
 */
export const collectDeletionTargetItemIds = (
  itemList: ItemListType[],
  parentIndex: number,
  childIndex?: number,
): Set<string> => {
  const target = itemList[parentIndex];
  if (!target) return new Set();

  const children = target.children ?? [];

  // 子項目単体の削除
  if (childIndex !== undefined) {
    const child = children[childIndex];
    return new Set(child?.id ? [child.id] : []);
  }

  // 親項目の削除。子を持つ親は通常マッピングから参照されないが、
  // 子を持たない時代に付けられた親IDへの参照が残っているデータもあるため、
  // 子のIDに加えて親IDも対象にする。
  const ids = new Set<string>();
  if (target.id) ids.add(target.id);
  for (const child of children) {
    if (child.id) ids.add(child.id);
  }

  return ids;
};

/** 参照の中に削除をブロックすべきものが含まれるか */
export const hasBlockingReference = (references: ItemReference[]): boolean =>
  references.some(reference => blockingKinds.has(reference.kind));

/** ブロック対象の参照だけを返す。ブロック案内に出すファイル名の列挙に使う */
export const getBlockingReferences = (references: ItemReference[]): ItemReference[] =>
  references.filter(reference => blockingKinds.has(reference.kind));

/**
 * ブロック対象でない参照だけを返す（絞り込みキー・反映先・履歴分離キー）。
 * 保存時に自動解除される参照であり、事前の注意表示に使う。
 */
export const getNonBlockingReferences = (references: ItemReference[]): ItemReference[] =>
  references.filter(reference => !blockingKinds.has(reference.kind));

/**
 * 参照を「ファイル名の{参照箇所}」という表示文字列にする。
 * 同じファイル・同じ種別の重複は除く（1マスタが複数の反映先に同一項目を使う場合など）。
 *
 * 項目削除モーダルでは「どの項目か」は利用者が押したボタンで自明なため、項目名は出さない。
 * 複数項目をまとめて扱う JSON 取り込みでは formatItemReferencesWithItemName を使う。
 */
export const formatItemReferences = (references: ItemReference[]): string[] => {
  const seen = new Set<string>();
  const formatted: string[] = [];

  for (const reference of references) {
    const text = `「${reference.sourceName}」の${itemReferenceKindLabels[reference.kind]}`;
    if (seen.has(text)) continue;
    seen.add(text);
    formatted.push(text);
  }

  return formatted;
};

/**
 * 参照を「項目名（ファイル名の{参照箇所}）」という表示文字列にする。
 *
 * JSON 取り込みのように複数項目がまとめて消える経路では、どの項目が問題なのかを
 * 名前で示す必要がある。項目名は item_list から引くのを優先し、
 * 見つからなければマッピングが保持しているラベルを使う
 * （マッピングのラベルは項目名変更に追随するが、旧データでは空のことがある）。
 *
 * @param references 対象の参照
 * @param itemLabels item_id → 項目名のマップ（buildItemLabelMapFromList の戻り値）
 */
export const formatItemReferencesWithItemName = (
  references: ItemReference[],
  itemLabels: ReadonlyMap<string, string>,
): string[] => {
  const seen = new Set<string>();
  const formatted: string[] = [];

  for (const reference of references) {
    const itemName = itemLabels.get(reference.itemId) || reference.itemLabel || reference.itemId;
    const text = `「${itemName}」（「${reference.sourceName}」の${itemReferenceKindLabels[reference.kind]}）`;
    if (seen.has(text)) continue;
    seen.add(text);
    formatted.push(text);
  }

  return formatted;
};

/**
 * item_list から item_id → 項目名のマップを作る。
 *
 * ラベル規則は buildItemLabelMap（master/mapping/syncLabels.ts）と揃える:
 *   - 子あり: 子IDごとに `${親} > ${子}`
 *   - 子なし: 親IDに `${親}`
 * 子を持つ親のIDにも親名を入れる（子を持たない時代の参照が残っている場合に名前を出せるようにする）。
 */
export const buildItemLabelMapFromList = (itemList: ItemListType[]): Map<string, string> => {
  const map = new Map<string, string>();

  for (const item of itemList) {
    if (item.id) map.set(item.id, item.parentValue);
    for (const child of item.children ?? []) {
      if (child.id) map.set(child.id, `${item.parentValue} > ${child.childValue}`);
    }
  }

  return map;
};

/**
 * item_list に存在する item_id をすべて集める。
 * 解除処理（cleanupItemReferences）が「まだ生きている項目」を判定するために使う。
 *
 * 親・子の両方のIDを含める。マッピングが参照するのは buildItemLabelMap の規則どおり
 * 「子ありなら子ID・子なしなら親ID」だが、ここは**消えていない item_id の集合**であり、
 * 参照され得るIDを取りこぼすと、生きている項目への参照を孤児と誤判定して消してしまう。
 * 子を持つ親のIDも、子を持たない時代に付けられた参照が残っている可能性がある。
 */
export const collectItemIds = (itemList: ItemListType[]): Set<string> => {
  const ids = new Set<string>();

  for (const item of itemList) {
    if (item.id) ids.add(item.id);
    for (const child of item.children ?? []) {
      if (child.id) ids.add(child.id);
    }
  }

  return ids;
};
